import { Injectable } from '@angular/core';
import { WebSocketService } from '../websocket.service';
import { ChatService } from '../chat/chat.service';
import { ProgressUpdate } from '../../models/progress-update';
import { LoggingService } from '../logging/logging.service';
import { TelemetryService } from '../telemetry/telemetry.service';

/**
 * Service that integrates WebSocket progress updates with the chat UI
 * This creates a bridge between operation progress and chat messages
 */
@Injectable({
  providedIn: 'root'
})
export class ChatProgressIntegrationService {
  // Track active operations by type to avoid duplicates
  private activeOperationTypes = new Set<string>();
  
  // Track operations that should be ignored for chat integration
  private ignoredOperationTypes = new Set<string>([
    'SYSTEM_REFRESH', 
    'HEALTH_CHECK',
    'DATA_SYNC'
    // Add any other operation types that shouldn't appear in chat
  ]);

  // Store progress history by message ID for tracing thought process
  private progressHistoryByMessage = new Map<string, ProgressUpdate[]>();

  constructor(
    private webSocketService: WebSocketService,
    private chatService: ChatService,
    private logger: LoggingService,
    private telemetryService: TelemetryService
  ) {}

  /**
   * Initialize the integration between progress updates and chat UI
   */
  public initialize(): void {
    this.logger.info('ChatProgressIntegrationService', 'Initializing chat progress integration');
    
    // Subscribe to all operations to show them in the chat
    this.webSocketService.operations$.subscribe(operations => {
      // Process each operation to potentially add a chat message
      operations.forEach(opSubject => {
        const update = opSubject.getValue();
        if (!update) return;
        
        this.handleProgressUpdate(update);
      });

      // Clean up any completed operations from our tracking
      this.cleanupCompletedOperations(operations);
    });
    
    this.telemetryService.trackEvent('chat_progress_integration_initialized');
  }

  /**
   * Handle a progress update and integrate it with the chat UI
   */
  private handleProgressUpdate(update: ProgressUpdate): void {
    // Skip ignored operation types
    if (this.ignoredOperationTypes.has(update.type)) {
      return;
    }
    
    // Skip if we're already tracking this operation type
    if (this.activeOperationTypes.has(update.type)) {
      // Track progress steps to build a thought trace
      this.storeProgressUpdate(update.type, update);
      return;
    }

    this.logger.info('ChatProgressIntegrationService', `New operation type detected: ${update.type}`);
    
    // Add this operation type to our tracking
    this.activeOperationTypes.add(update.type);
    
    // Create a chat message associated with this operation type
    this.chatService.addBotMessageWithOperationType(update.type);
    
    // Start tracking progress steps for this operation
    this.storeProgressUpdate(update.type, update);
    
    this.telemetryService.trackEvent('chat_operation_progress_started', {
      operationType: update.type,
      operationId: update.operationId,
      initialProgress: update.progress
    });
  }
  
  /**
   * Store a progress update for thought tracing
   */
  private storeProgressUpdate(operationType: string, update: ProgressUpdate): void {
    // Create or get the history array for this operation
    if (!this.progressHistoryByMessage.has(operationType)) {
      this.progressHistoryByMessage.set(operationType, []);
    }
    
    const history = this.progressHistoryByMessage.get(operationType)!;
    
    // Don't add duplicate consecutive messages
    if (history.length > 0) {
      const lastUpdate = history[history.length - 1];
      if (lastUpdate.message === update.message && lastUpdate.type === update.type) {
        // Just update the progress
        lastUpdate.progress = update.progress;
        return;
      }
    }
    
    // Add to history
    history.push({...update});
    
    // Limit history size to prevent memory issues
    if (history.length > 20) {
      history.shift();
    }
  }
  
  /**
   * Get progress history for a specific operation type
   */
  public getProgressHistory(operationType: string): ProgressUpdate[] {
    return this.progressHistoryByMessage.get(operationType) || [];
  }

  /**
   * Clean up any operation types that are no longer active
   */
  private cleanupCompletedOperations(operations: Map<string, any>): void {
    // Find operation types that are still active
    const activeTypes = new Set<string>();
    
    operations.forEach(opSubject => {
      const update = opSubject.getValue();
      if (update) {
        activeTypes.add(update.type);
      }
    });
    
    // Remove any types we're tracking that are no longer active
    const completedTypes: string[] = [];
    
    for (const type of this.activeOperationTypes) {
      if (!activeTypes.has(type)) {
        this.logger.info('ChatProgressIntegrationService', `Operation completed: ${type}`);
        this.activeOperationTypes.delete(type);
        completedTypes.push(type);
      }
    }
    
    // For operations that completed with no content, add a generic completion message
    for (const type of completedTypes) {
      this.telemetryService.trackEvent('chat_operation_auto_completed', {
        operationType: type
      });
    }
  }
  
  /**
   * Handle a completed operation by updating the associated chat message
   * Call this method when an operation produces a result that should be shown in chat
   * 
   * @param operationType The type of operation that completed
   * @param result The result content to show in the chat message
   */
  public handleOperationCompleted(operationType: string, result: string): void {
    this.logger.info('ChatProgressIntegrationService', `Operation completed with result: ${operationType}`);
    
    // Remove from active operations
    this.activeOperationTypes.delete(operationType);
    
    // Update the chat message with the result
    if (result) {
      this.chatService.updateLastBotMessage(result);
      
      this.telemetryService.trackEvent('chat_operation_completed_with_result', {
        operationType: operationType,
        resultLength: result.length
      });
    }
    
    // Keep the history for thought trace display
  }
  
  /**
   * Check if an operation type is currently active
   */
  public isOperationActive(operationType: string): boolean {
    return this.activeOperationTypes.has(operationType);
  }
  
  /**
   * Get all currently active operation types
   */
  public getActiveOperationTypes(): string[] {
    return Array.from(this.activeOperationTypes);
  }
}